import React from 'react';
import { useNavigate } from 'react-router-dom';
import './HeroSection.css';
import './ServiceSection.css';
import './AboutSection.css';
import './PricingSection.css';
import './PortfolioSection.css';
import './ExperienceSection.css';
import img1 from '../assets/img1.png';
import img2 from '../assets/img2.png';
import img3 from '../assets/img3.png';

const services = [
  {
    icon: '🤖',
    title: 'AI Chatbots & Virtual Assistants',
    desc: '24/7 digital agents to automate, qualify and support real business growth.'
  },
  {
    icon: '🔗',
    title: 'API Integration & Automation',
    desc: 'Seamless data flows and automation — powering tomorrow’s digital businesses.'
  },
  {
    icon: '🧩',
    title: 'ERP & CRM Solutions',
    desc: 'Centralize operations and unlock smarter decisions with tailored software.'
  },
  {
    icon: '🎨',
    title: 'UI/UX Design & Prototyping',
    desc: 'Standout, human-centered design for delightful digital experiences.'
  },
  {
    icon: '⚙️',
    title: 'Custom Software & SaaS Solutions',
    desc: 'From MVPs to scalable SaaS, tech that helps you leap ahead.'
  },
  {
    icon: '📱',
    title: 'Mobile App Development',
    desc: 'Powerful apps, beautiful UI, frictionless journeys on every device.'
  }
];

const aboutBadges = [
  { icon: '🌟', label: '98% Client Satisfaction' },
  { icon: '🚀', label: '20+ Successful Projects' },
  { icon: '🧠', label: '1.5+ Years of Experience' },
];
const pricingPlans = [
  {
    name: 'Starter',
    price: '₹5,000',
    features: [
      'Basic website (1-3 pages)',
      'Responsive design',
      'Basic contact form',
      'Social media integration',
      '1 revision',
      'Email support for 1 month',
    ],
    cta: 'Get Started',
  },
  {
    name: 'Standard',
    price: '₹15,000',
    features: [
      'Up to 10 pages',
      'Custom design and layout',
      'Basic SEO optimization',
      'Google Analytics setup',
      'Contact form with basic CRM integration',
      '1 month of maintenance',
      '3 revisions',
    ],
    cta: 'Get Started',
  },
  {
    name: 'Premium',
    price: '₹25,000',
    features: [
      'Unlimited pages',
      'Advanced SEO (on-page and off-page)',
      'E-commerce functionality (up to 50 products)',
      'Custom content management system (CMS)',
      'Security features (SSL, firewalls, etc.)',
      '1 month of website maintenance',
      'Unlimited revisions',
      'Dedicated support',
    ],
    cta: 'Get Started',
  },
];
const portfolioProjects = [
  {
    image: img1,
    name: 'OneAIChat',
    url: 'https://oneaichat.com/',
    desc: 'A next-gen AI chat platform integrating multiple LLMs (GPT, open-source, custom-trained models). Provides model selection, subscription management, SSO/login, and real-time chat. Scalable SaaS for individuals & enterprises.',
    stack: 'React, Node.js, Express, MongoDB, OpenAI & custom LLMs',
  },
  {
    image: img2,
    name: 'Chocstar.nl',
    url: 'https://chocstar.nl/',
    desc: 'Chocstar.nl is a Netherlands-based e-commerce store for personalized, handmade chocolates. I built a fast, responsive platform enabling users to customize chocolate gifts with messages, logos, and packaging. Optimized product display, checkout, and mobile UX. Features include B2B gifting, logo branding, and same-day shipping integration.',
    stack: 'Next.js, React, Node.js, MongoDB, Stripe, Responsive Web Design',
  },
  {
    image: img3,
    name: 'HeliosWatchStore.com',
    url: 'https://helioswatchstore.com/',
    desc: 'Worked on HeliosWatchStore.com, an online shop specializing in luxury, fashion, and designer watches. Improved UX by redesigning product category pages, refining browsing & filtering, and optimizing checkout. Ensured superb mobile/tablet experience, implemented secure payments, and optimized site speed for better conversions.',
    stack: 'Next.js, React, Node.js, Headless CMS, Responsive Web, Payment Gateway',
  },
 
];

const experienceStats = [
  { big: '5+', label: 'Clients' },
  { big: '10+', label: 'UI/UX Designs' },
  { big: '10+', label: 'Projects' },
  { big: '2+', label: 'Themes' },
];

const Home: React.FC = () => {
  const navigate = useNavigate();
  return (
    <>
      <section className="hero-cn-container">
        <div className="hero-cn-badge big-badge">Welcome to <span className="gradient-text">CodeNova AI</span></div>
        <div className="hero-cn-title">
          <span className="hero-cn-head">Empowering Smart Businesses with AI & Web Excellence</span>
        </div>
        <div className="hero-cn-desc desc-muted">
          At CodeNova AI, we design intelligent solutions — from custom AI agents to seamless digital automation — <br />helping you grow, innovate, and succeed round the clock.
        </div>
      </section>
      <section className="services-section premium-services">
        <h2 className="services-title gradient-text">Our Capabilities</h2>
        <p className="services-lead">
          At CodeNova AI, we unlock business potential by combining innovative technology with creative vision.<br />Partner with us to experience next-level growth, smarter workflows, and digital excellence at every stage of your journey.
        </p>
        <div className="services-grid premium-grid">
          {services.map((service, idx) => (
            <div className="service-card premium-card" key={idx}>
              <div className="service-icon-glass">{service.icon}</div>
              <h3>{service.title}</h3>
              <p>{service.desc}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="about-cn-section">
        <h2 className="about-cn-title gradient-text">About CodeNova AI</h2>
        <div className="about-cn-desc">
          At CodeNova AI, we’re passionate about bridging ideas and innovation. We help brands level up with smart AI, stunning web experiences, and seamless automation. <br />From concept to launch, we bring expertise, creativity, and a relentless drive for results — empowering your business every step of the way.
        </div>
        <div className="about-cn-badges-row">
          {aboutBadges.map((badge, idx) => (
            <div className="about-cn-badge" key={idx}>
              <span className="badge-icon">{badge.icon}</span>
              <span className="badge-label">{badge.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing-cn-section">
        <div className="pricing-badge">Website Pricing &amp; Packages</div>
        <h2 className="pricing-title gradient-text">Affordable Website Development Plans</h2>
        <div className="pricing-lead">
          Get professional web design, SEO, and branding at competitive prices. Our packages are designed for businesses of all sizes, ensuring high-quality digital solutions within your budget. Explore our pricing for custom websites, SEO services, and eCommerce development.
        </div>
        <div className="pricing-grid">
          {pricingPlans.map((plan, i) => (
            <div className="pricing-card" key={plan.name}>
              <div className="plan-name">{plan.name}</div>
              <div className="plan-price">{plan.price}</div>
              <ul className="plan-features">
                {plan.features.map((f, ix) => (
                  <li key={ix}><span className="plan-feature-icon">✔️</span> {f}</li>
                ))}
              </ul>
              <button className="plan-cta-btn" onClick={() => navigate('/contact')}>{plan.cta} →</button>
            </div>
          ))}
        </div>
      </section>

      <section className="portfolio-cn-section">
        <div className="portfolio-badge">Our Portfolio</div>
        <h2 className="portfolio-title gradient-text">Let’s See Our Best Work</h2>
        <div className="portfolio-lead">
          We build next-gen digital products for a changing world. <br className="hide-on-mobile" />Here’s a few projects that showcase our talent for innovation, design, and seamless execution.
        </div>
        <div className="portfolio-grid">
          {portfolioProjects.map((project, i) => (
            <div className="portfolio-card" key={project.name}>
              <div className="portfolio-img" style={{backgroundImage:`url('${project.image}')`}}></div>
              <div className="portfolio-meta">
                {project.url ? (
                  <a href={project.url} className="portfolio-project-name gradient-text" target="_blank" rel="noopener noreferrer">{project.name}</a>
                ) : (
                  <div className="portfolio-project-name gradient-text">{project.name}</div>
                )}
                <div className="portfolio-desc">{project.desc}</div>
                <div className="portfolio-stack">{project.stack}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="experience-cn-section">
        <div className="experience-badge">Our Experience</div>
        <h2 className="experience-title">A Proven History Of Success In Web Development Technology</h2>
        <div className="experience-cards-row">
          {experienceStats.map((stat, i) => (
            <div className="experience-card" key={i}>
              <div className="experience-stat-big">{stat.big}</div>
              <div className="experience-stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>
      {/* footer removed here, now global in App.tsx */}
    </>
  );
};
export default Home;
